/**
 * MessageFeedback Component
 *
 * Provides thumbs up/down feedback controls for individual messages.
 * Allows users to indicate response quality and optionally provide text feedback.
 */

import { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { Button } from '@/components/ui'
import { cn } from '@/utils'
import { ThumbsUp, ThumbsDown, MessageSquare, X, Send } from 'lucide-react'

export type FeedbackRating = 'positive' | 'negative' | null

export interface MessageFeedbackData {
  messageId: string
  rating: FeedbackRating
  comment?: string
  timestamp: number
}

interface MessageFeedbackProps {
  messageId: string
  currentRating?: FeedbackRating
  onFeedback: (data: MessageFeedbackData) => void
  disabled?: boolean
  showCommentOption?: boolean
  compact?: boolean
  className?: string
}

export function MessageFeedback({
  messageId,
  currentRating,
  onFeedback,
  disabled = false,
  showCommentOption = true,
  compact = false,
  className,
}: MessageFeedbackProps) {
  const [rating, setRating] = useState<FeedbackRating>(currentRating ?? null)
  const [showCommentInput, setShowCommentInput] = useState(false)
  const [comment, setComment] = useState('')
  const [hasSubmitted, setHasSubmitted] = useState(false)

  const handleRating = (newRating: FeedbackRating) => {
    if (disabled || hasSubmitted) return

    // Toggle if same rating clicked
    const finalRating = rating === newRating ? null : newRating
    setRating(finalRating)

    // Only show comment input for negative feedback
    if (finalRating === 'negative' && showCommentOption) {
      setShowCommentInput(true)
    } else {
      setShowCommentInput(false)
      // Submit immediately for positive feedback or clearing
      onFeedback({
        messageId,
        rating: finalRating,
        timestamp: Date.now(),
      })
      if (finalRating) {
        setHasSubmitted(true)
      }
    }
  }

  const handleSubmitComment = () => {
    onFeedback({
      messageId,
      rating,
      comment: comment.trim() || undefined,
      timestamp: Date.now(),
    })
    setShowCommentInput(false)
    setHasSubmitted(true)
  }

  const handleCancelComment = () => {
    setShowCommentInput(false)
    setComment('')
    // Keep the rating but don't submit
  }

  // Show thank you message after submission
  if (hasSubmitted && !showCommentInput) {
    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        className={cn(
          'flex items-center gap-1.5 text-xs text-muted-foreground',
          className
        )}
      >
        <span>Thanks for your feedback!</span>
        {rating === 'positive' && (
          <ThumbsUp className="w-3 h-3 text-success fill-success" />
        )}
        {rating === 'negative' && (
          <ThumbsDown className="w-3 h-3 text-destructive fill-destructive" />
        )}
      </motion.div>
    )
  }

  return (
    <div className={cn('flex flex-col gap-2', className)}>
      {/* Rating buttons */}
      <div className="flex items-center gap-1">
        <motion.button
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.95 }}
          onClick={() => handleRating('positive')}
          disabled={disabled}
          className={cn(
            'p-1.5 rounded-md transition-colors',
            'focus:outline-hidden focus:ring-2 focus:ring-ring focus:ring-offset-1',
            rating === 'positive'
              ? 'bg-success/20 text-success'
              : 'text-muted-foreground hover:text-success hover:bg-success/10',
            disabled && 'opacity-50 cursor-not-allowed'
          )}
          aria-label="Good response"
          title="Good response"
        >
          <ThumbsUp
            className={cn(
              compact ? 'w-3.5 h-3.5' : 'w-4 h-4',
              rating === 'positive' && 'fill-current'
            )}
          />
        </motion.button>

        <motion.button
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.95 }}
          onClick={() => handleRating('negative')}
          disabled={disabled}
          className={cn(
            'p-1.5 rounded-md transition-colors',
            'focus:outline-hidden focus:ring-2 focus:ring-ring focus:ring-offset-1',
            rating === 'negative'
              ? 'bg-destructive/20 text-destructive'
              : 'text-muted-foreground hover:text-destructive hover:bg-destructive/10',
            disabled && 'opacity-50 cursor-not-allowed'
          )}
          aria-label="Poor response"
          title="Poor response"
        >
          <ThumbsDown
            className={cn(
              compact ? 'w-3.5 h-3.5' : 'w-4 h-4',
              rating === 'negative' && 'fill-current'
            )}
          />
        </motion.button>

        {showCommentOption && !showCommentInput && rating && (
          <motion.button
            initial={{ opacity: 0, x: -10 }}
            animate={{ opacity: 1, x: 0 }}
            whileHover={{ scale: 1.05 }}
            onClick={() => setShowCommentInput(true)}
            className="p-1.5 rounded-md text-muted-foreground hover:text-primary hover:bg-primary/10 transition-colors"
            aria-label="Add comment"
            title="Add comment"
          >
            <MessageSquare className={compact ? 'w-3.5 h-3.5' : 'w-4 h-4'} />
          </motion.button>
        )}
      </div>

      {/* Comment input */}
      <AnimatePresence>
        {showCommentInput && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="overflow-hidden"
          >
            <div className="flex flex-col gap-2 p-2 bg-muted rounded-lg">
              <textarea
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                placeholder="What could be improved? (optional)"
                rows={2}
                className={cn(
                  'w-full px-2 py-1.5 text-sm rounded-md',
                  'bg-background',
                  'border border-border',
                  'focus:outline-hidden focus:ring-2 focus:ring-ring',
                  'placeholder:text-muted-foreground resize-none'
                )}
              />
              <div className="flex items-center justify-end gap-2">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleCancelComment}
                  leftIcon={<X className="w-3 h-3" />}
                >
                  Cancel
                </Button>
                <Button
                  variant="default"
                  size="sm"
                  onClick={handleSubmitComment}
                  leftIcon={<Send className="w-3 h-3" />}
                >
                  Submit
                </Button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}

/**
 * Inline feedback variant - minimal footprint for message bubbles
 */
interface InlineFeedbackProps {
  messageId: string
  onFeedback: (data: MessageFeedbackData) => void
  className?: string
}

export function InlineMessageFeedback({
  messageId,
  onFeedback,
  className,
}: InlineFeedbackProps) {
  const [submitted, setSubmitted] = useState(false)
  const [rating, setRating] = useState<FeedbackRating>(null)

  const handleQuickFeedback = (newRating: FeedbackRating) => {
    setRating(newRating)
    setSubmitted(true)
    onFeedback({
      messageId,
      rating: newRating,
      timestamp: Date.now(),
    })
  }

  if (submitted) {
    return (
      <motion.span
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className={cn('text-xs text-muted-foreground', className)}
      >
        {rating === 'positive' ? (
          <ThumbsUp className="w-3 h-3 text-success inline" />
        ) : (
          <ThumbsDown className="w-3 h-3 text-destructive inline" />
        )}
      </motion.span>
    )
  }

  return (
    <span
      className={cn(
        'inline-flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity',
        className
      )}
    >
      <button
        onClick={() => handleQuickFeedback('positive')}
        className="p-0.5 text-muted-foreground hover:text-success transition-colors"
        aria-label="Good response"
      >
        <ThumbsUp className="w-3 h-3" />
      </button>
      <button
        onClick={() => handleQuickFeedback('negative')}
        className="p-0.5 text-muted-foreground hover:text-destructive transition-colors"
        aria-label="Poor response"
      >
        <ThumbsDown className="w-3 h-3" />
      </button>
    </span>
  )
}
